/**
 * Public read-only share links for knowledgebase notes.
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { DocumentShare } from "@shared/schema";

export interface PublicDocumentPayload {
  id: string;
  title: string;
  content: string | null;
  itemType: "note";
}

export interface DocumentShareLinkValidationResult {
  isValid: boolean;
  message: string;
  share: Pick<DocumentShare, "id" | "documentId" | "shareToken"> | null;
  document: PublicDocumentPayload | null;
}

export interface DocumentPublicLinkResult {
  shareToken: string;
  documentId: string;
  url: string;
}

function parseValidationPayload(data: unknown): DocumentShareLinkValidationResult {
  let payload: unknown = data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return { isValid: false, message: "Invalid response", share: null, document: null };
    }
  }
  if (!payload || typeof payload !== "object") {
    return { isValid: false, message: "Invalid response", share: null, document: null };
  }
  const o = payload as Record<string, unknown>;
  const isValid = o.isValid === true;
  const message = typeof o.message === "string" ? o.message : isValid ? "OK" : "Invalid link";

  let share: DocumentShareLinkValidationResult["share"] = null;
  if (o.share && typeof o.share === "object") {
    const s = o.share as Record<string, unknown>;
    share = {
      id: String(s.id ?? ""),
      documentId: String(s.documentId ?? s.document_id ?? ""),
      shareToken: String(s.shareToken ?? s.share_token ?? ""),
    };
  }

  let document: PublicDocumentPayload | null = null;
  if (o.document && typeof o.document === "object") {
    const d = o.document as Record<string, unknown>;
    document = {
      id: String(d.id ?? ""),
      title: String(d.title ?? "Untitled"),
      content: d.content != null ? String(d.content) : null,
      itemType: "note",
    };
  }

  return { isValid, message, share, document };
}

function parseUpsertPayload(data: unknown): { shareToken: string; documentId: string } {
  let payload: unknown = data;
  if (typeof payload === "string") {
    payload = JSON.parse(payload);
  }
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid response from server");
  }
  const o = payload as Record<string, unknown>;
  const shareToken = String(o.shareToken ?? o.share_token ?? "");
  const documentId = String(o.documentId ?? o.document_id ?? "");
  if (!shareToken || !documentId) {
    throw new Error("Invalid response from server");
  }
  return { shareToken, documentId };
}

class DocumentSharingService {
  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  buildPublicUrl(shareToken: string): string {
    return `${window.location.origin}/documents/share/${shareToken}`;
  }

  async validatePublicLink(token: string): Promise<DocumentShareLinkValidationResult> {
    const { data, error } = await supabase.rpc("document_share_validate", {
      p_token: token,
    });
    if (error) {
      return {
        isValid: false,
        message: error.message || "Failed to validate link",
        share: null,
        document: null,
      };
    }
    return parseValidationPayload(data);
  }

  async getOrCreatePublicLink(documentId: string): Promise<DocumentPublicLinkResult> {
    await this.getCurrentUserId();
    const { data, error } = await supabase.rpc("document_share_upsert", {
      p_document_id: documentId,
    });
    if (error) {
      throw new Error(error.message || "Failed to create public link");
    }
    const { shareToken, documentId: docId } = parseUpsertPayload(data);
    return {
      shareToken,
      documentId: docId,
      url: this.buildPublicUrl(shareToken),
    };
  }

  validationDocumentToKnowledgeBaseItem(
    doc: PublicDocumentPayload,
    ownerUserId?: string
  ): import("@/services/knowledgeBaseService").KnowledgeBaseItem {
    return {
      id: doc.id,
      userId: ownerUserId ?? "",
      title: doc.title,
      content: doc.content,
      type: "note",
    };
  }
}

export const documentSharingService = new DocumentSharingService();
